import { SORTING_POLLS } from '@/constants';
import { NOTIFICATION_TYPE } from '@/modules/notification/types';
import type { OpinionPoll } from '@/modules/opinions/types';
import { POLL, VOTE_PARAMS } from '@/modules/polls/types';
import { RootStore } from '@/store';
import { apiRequest, formatTokenAmount } from '@/utils';
import { analytics } from '@/utils/analytics';
import { HELIKA_EVENT_TYPE } from '@/utils/helika';
import { storage } from '@/utils/storage';
import { t } from 'i18next';
import { makeAutoObservable, reaction } from 'mobx';

export const TABS_MAP = {
  CREATE: 'create',
  OPINIONS: 'opinions',
  TOURNAMENTS: 'tournaments',
  POLLS: 'polls',
  GAMES: 'games',
  LEADERBOARD: 'leaderboard',
  EARN: 'earn',
} as const;

export const FILTER_MAP = {
  ACTIVE: 'active',
  MY_POLLS: 'my_polls',
  FINISHED: 'finished',
  MY_FINISHED_POLLS: 'my_finished_polls',
} as const;

export const FILTER_MAP_TOURNAMENTS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
} as const;

export type TAB = (typeof TABS_MAP)[keyof typeof TABS_MAP];
export type FILTER = (typeof FILTER_MAP)[keyof typeof FILTER_MAP];
export type FILTER_TOURNAMENTS =
  | (typeof FILTER_MAP_TOURNAMENTS)[keyof typeof FILTER_MAP_TOURNAMENTS]
  | null;

export class PollsStore {
  readonly rootStore: RootStore;

  public activeFilter: FILTER = FILTER_MAP.ACTIVE;
  public activeFilterTournaments: FILTER_TOURNAMENTS = null;
  public activePolls: POLL[] = [];
  public myPolls: POLL[] = [];
  public finishedPolls: POLL[] = [];
  public myFinishedPolls: POLL[] = [];
  public topics: {
    polls: string[];
    opinions: string[];
    tournaments: string[];
  } = {
    polls: [],
    opinions: [],
    tournaments: [],
  };
  public selectedTopics: {
    polls: string[];
    opinions: string[];
    tournaments: string[];
  } = {
    polls: [],
    opinions: [],
    tournaments: [],
  };
  public currentSorting: SORTING_POLLS = SORTING_POLLS.ASC; // for polls
  public isPollsSortModalVisible: boolean = false;
  public isOpinionsSortModalVisible: boolean = false;
  public isTournamentsSortModalVisible: boolean = false;

  public expandedPolls: Map<POLL['_id'], POLL> = new Map();

  // poll page
  public currentPoll: POLL | null = null;

  // opinion page
  public currentOpinionId: string | null = null;
  public currentOpinion: OpinionPoll | null = null;

  public votedOpinionsCount = 0;
  public uniqueKeyToRefetchList: number = Date.now();

  constructor({ rootStore }: { rootStore: RootStore }) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
    // todo check ?
    reaction(
      () => this.activeFilter,
      (val) => {
        if (val === FILTER_MAP.ACTIVE) {
          this.setCurrentSorting(SORTING_POLLS.ASC);
        } else {
          this.setCurrentSorting(SORTING_POLLS.DESC);
        }
      },
    );
  }

  public updateUniqueKeyToRefetchList = () => {
    this.uniqueKeyToRefetchList = Date.now();
  };

  public increaseOpinionsCount = () => {
    this.votedOpinionsCount++;
  };

  public setVotedOpinionsCount = (val: number) => {
    this.votedOpinionsCount = val;
  };

  public setCurrentSorting = (sorting: SORTING_POLLS) => {
    this.currentSorting = sorting;
  };

  public setActiveFilter = (filter: FILTER) => {
    this.activeFilter = filter;
  };

  public setActiveFilterTournaments = (filter: FILTER_TOURNAMENTS) => {
    this.activeFilterTournaments = filter;
  };

  private totalMixin = (poll: POLL) => {
    const amount = poll?.jettonsTVL
      ? Object.keys(poll.jettonsTVL).reduce((acc, id) => {
          const jetton = this.rootStore.walletStore.mappedAssets[id];
          if (!jetton) return acc;
          return (
            acc +
            (poll.jettonsTVL[id] / 10 ** jetton.decimals) *
              jetton?.balance?.rate
          );
        }, 0)
      : 0;

    return {
      ...poll,
      total: {
        amount,
        _amount: formatTokenAmount(amount, 0),
        ticker: 'USD',
      },
    };
  };

  private sortPolls = (polls: POLL[]) => {
    return [...polls].sort((a, b) => {
      if (a.state === 'allocated' && b.state !== 'allocated') return -1;
      if (a.state !== 'allocated' && b.state === 'allocated') return 1;

      if (a.partnerUrl && !b.partnerUrl) return -1;
      if (!a.partnerUrl && b.partnerUrl) return 1;

      if (a.state !== 'voted' && b.state === 'voted') return -1;
      if (a.state === 'voted' && b.state !== 'voted') return 1;

      return 0;
    });
  };

  private setActivePolls = (polls: POLL[]) => {
    this.activePolls = this.sortPolls(polls);
  };

  public getActivePolls = async (sorting: SORTING_POLLS) => {
    const polls =
      sorting === SORTING_POLLS.VOTERS
        ? await this.getPolls(
            100,
            { voters: SORTING_POLLS.DESC },
            { expired: false, mode: 'or', state: ['allocated'] },
          )
        : await this.getPolls(
            100,
            { expiresAt: sorting },
            { expired: false, mode: 'or', state: ['allocated'] },
          );
    this.setActivePolls(polls);
  };

  private setMyPolls = (polls: POLL[]) => {
    this.myPolls = this.sortPolls(polls);
  };

  public getMyPolls = async (sorting: SORTING_POLLS) => {
    const polls =
      sorting === SORTING_POLLS.VOTERS
        ? await this.getPolls(
            100,
            { voters: SORTING_POLLS.DESC },
            { expired: false, state: ['allocated', 'voted', 'claimed'] },
          )
        : await this.getPolls(
            100,
            { expiresAt: sorting },
            { expired: false, state: ['allocated', 'voted', 'claimed'] },
          );

    this.setMyPolls(polls);
  };

  public getAuthorPolls = async ({
    userId,
    limit = 100,
    skip = 0,
    lang = 'en',
  }: {
    userId: string;
    limit: number;
    skip?: number;
    lang?: string;
  }) => {
    return await this.getPolls(
      limit,
      { expiresAt: SORTING_POLLS.DESC },
      { author: userId },
      skip,
      lang,
    );
  };

  private getPolls = async (
    limit: number = 100,
    sorting: { expiresAt: SORTING_POLLS } | { voters: SORTING_POLLS },
    filters: {
      expired?: boolean;
      mode?: 'or';
      state?: ('allocated' | 'voted' | 'claimed')[];
      author?: string;
    },
    skip?: number,
    lang?: string | null,
  ): Promise<POLL[]> => {
    if (!skip) skip = 0;
    if (!lang) lang = (await storage.getItem('lang')) || 'en';

    const [polls] = await apiRequest(
      [
        {
          path: '/v1/polls/list',
          method: 'POST',
          body: {
            limit,
            lang,
            sorting,
            filters,
            skip,
          },
        },
      ],
      this.rootStore.errorStore,
    );

    return Array.isArray(polls) ? polls.map(this.totalMixin) : [];
  };

  private setFinishedPolls = (polls: POLL[]) => {
    this.finishedPolls = this.sortPolls(polls);
  };

  public getFinishedPolls = async (sorting: SORTING_POLLS) => {
    const polls =
      sorting === SORTING_POLLS.VOTERS
        ? await this.getPolls(
            100,
            { voters: SORTING_POLLS.DESC },
            { expired: true },
          )
        : await this.getPolls(100, { expiresAt: sorting }, { expired: true });
    this.setFinishedPolls(polls);
  };

  private setMyFinishedPolls = (polls: POLL[], sorting: SORTING_POLLS) => {
    this.myFinishedPolls = [
      ...polls.sort((a, b) => {
        if (sorting === SORTING_POLLS.ASC) return a.expiresAt - b.expiresAt;
        if (sorting === SORTING_POLLS.DESC) return b.expiresAt - a.expiresAt;
        if (sorting === SORTING_POLLS.VOTERS) return b.voters - a.voters;
        return 0;
      }),
    ];
  };

  public getMyFinishedPolls = async (sorting: SORTING_POLLS) => {
    const claimedPolls =
      sorting === SORTING_POLLS.VOTERS
        ? await this.getPolls(
            100,
            { voters: SORTING_POLLS.DESC },
            { expired: true, state: ['claimed'] },
          )
        : await this.getPolls(
            100,
            { expiresAt: sorting },
            { expired: true, state: ['claimed'] },
          );
    const votedPolls =
      sorting === SORTING_POLLS.VOTERS
        ? await this.getPolls(
            100,
            { voters: SORTING_POLLS.DESC },
            { expired: true, state: ['allocated', 'voted'] },
          )
        : await this.getPolls(
            100,
            { expiresAt: sorting },
            { expired: true, state: ['allocated', 'voted'] },
          );

    this.setMyFinishedPolls([...votedPolls, ...claimedPolls], sorting);
  };

  private setTopics = (topics: [], type: 'polls' | 'opinions') => {
    this.topics[type] = (topics || []).map(
      ({ topic }: { topic: string }) => topic,
    );
  };

  public getTopics = async ({
    type = 'polls',
  }: {
    type: 'polls' | 'opinions';
  }) => {
    const [topics] = await apiRequest(
      [
        {
          path: `/v1/misc/topics/${type}`,
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );
    if (!topics.isError) this.setTopics(topics, type);
  };

  public filterPollsByTopics = (polls: POLL[], topics: string[]) => {
    if (topics.length === 0) return polls;

    return polls.filter((poll) => {
      const matchedTopics: Set<string> = new Set();
      poll.topics.forEach((t: string) => {
        if (topics.includes(t)) {
          matchedTopics.add(t);
        }
      });
      return matchedTopics.size > 0;
    });
  };

  public setSelectedTopics = (
    topics: string[],
    type: 'polls' | 'opinions' | 'tournaments',
  ) => {
    this.selectedTopics[type] = topics;
  };

  public setCurrentOpinionId = (id: string) => {
    this.currentOpinionId = id;
  };

  private fetchPoll = async (pollId: string) => {
    const lang = await storage.getItem('lang');
    const [poll] = await apiRequest(
      [
        {
          path: '/v1/polls/' + pollId + '/' + lang,
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );
    return this.totalMixin(poll);
  };

  private fetchOpinion = async (opinionId: string) => {
    const lang = await storage.getItem('lang');
    const [opinion] = await apiRequest(
      [
        {
          path: '/v1/opinion-polls/' + opinionId + '/' + lang,
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );
    return opinion;
  };

  public getCurrentPoll = async (pollId: string) => {
    this.currentPoll = await this.fetchPoll(pollId);
  };

  public getCurrentOpinion = async (opinionId: string) => {
    this.currentOpinion = await this.fetchOpinion(opinionId);
  };

  public getExpandedPoll = async (pollId: string) => {
    const poll = await this.fetchPoll(pollId);
    this.expandedPolls.set(pollId, poll);
  };

  public votePoll = async ({
    pollId,
    itemId,
    amount,
    jetton,
    jettonAmount,
  }: VOTE_PARAMS) => {
    const [res] = await apiRequest([
      {
        path: '/v1/polls/' + pollId + '/vote',
        method: 'POST',
        body:
          jettonAmount && jetton
            ? {
                amount,
                item: itemId,
                jetton: {
                  id: jetton._id,
                  amountRaw: Math.round(jettonAmount * 10 ** jetton.decimals),
                },
              }
            : {
                amount,
                item: itemId,
              },
      },
    ]);

    if (res.isError) {
      this.rootStore.notificationStore.setNotificationMessage(res.message);
      this.rootStore.notificationStore.setNotificationType(
        NOTIFICATION_TYPE.COMMON,
      );
      return;
    } else {
      this.rootStore.notificationStore.setNotificationMessage(
        t('Succesfuly voted') + '!',
      );

      analytics.sendEvent({
        event_type: HELIKA_EVENT_TYPE.GAMEPLAY,
        event: {
          event_sub_type: 'poll_vote',
          event_details: {
            event_label: 'Poll voted',
            event_action: 'vote',
          },
          poll_id: pollId,
        },
      });
    }
    this.rootStore.notificationStore.setNotificationType(
      NOTIFICATION_TYPE.COMMON,
    );

    await Promise.all([
      this.rootStore.accountStore.getAccountData(),
      this.rootStore.walletStore.getJettonBalances(),
      this.getCurrentPoll(pollId),
      this.getExpandedPoll(pollId),
    ]);
  };

  public claimPoll = async (pollId: string) => {
    const [ids] = await apiRequest(
      [
        {
          path: '/v1/allocations/list',
          method: 'POST',
          body: {
            poll: pollId,
          },
        },
      ],
      this.rootStore.errorStore,
    );

    const [res] = await apiRequest([
      {
        path: '/v1/allocations/claim/' + ids[0]._id,
        method: 'POST',
        body: {
          poll: pollId,
        },
      },
    ]);

    if (res.isError) {
      this.rootStore.notificationStore.setNotificationMessage(res.message);
      this.rootStore.notificationStore.setNotificationType(
        NOTIFICATION_TYPE.COMMON,
      );
      return;
    }

    await Promise.all([
      this.rootStore.accountStore.getAccountData(),
      this.rootStore.walletStore.getJettonBalances(),
      this.getCurrentPoll(pollId),
    ]);
    this.updateAllocatedAndClaimedInPoll(pollId);
  };

  private updateAllocatedAndClaimedInPoll = (id: string) => {
    const claimedPollIdx = this.activePolls.findIndex((p) => p._id === id);
    if (this.currentPoll && this.activePolls[claimedPollIdx]) {
      this.activePolls[claimedPollIdx].tokensClaimed =
        this.currentPoll.tokensClaimed;
      this.activePolls[claimedPollIdx].tokensAllocated =
        this.currentPoll.tokensAllocated;
    }
  };

  public setIsPollsSortModalVisible = (isVisible: boolean) => {
    this.isPollsSortModalVisible = isVisible;
  };

  public setIsOpinionsSortModalVisible = (isVisible: boolean) => {
    this.isOpinionsSortModalVisible = isVisible;
  };

  public setIsTournamentsSortModalVisible = (isVisible: boolean) => {
    this.isTournamentsSortModalVisible = isVisible;
  };
}
